import { Text, SimpleGrid, Box, Button } from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';

import { changeDwellingRatingInformation } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

interface InteriorConstructionProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const InteriorConstruction = ({
  dwelling,
  index,
}: InteriorConstructionProps) => {
  const dispatch = useAppDispatch();
  const isDisabled = useIsBinderDisabled('homePersonalForm');

  const onInteriorWallConstructionChange = (
    event: any,
    itemIndex: number,
    field: string,
  ) => {
    const { value } = event.target;
    const currentItems =
      dwelling.rating_information.interior_wall_construction || [];
    const updatedItems = [...currentItems];

    if (!updatedItems[itemIndex]) {
      updatedItems[itemIndex] = { type: '', percent: '' };
    }

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      [field]: value,
    };

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_wall_construction',
        value: updatedItems,
      }),
    );
  };

  const onInteriorWallHeightChange = (
    event: any,
    itemIndex: number,
    field: string,
  ) => {
    const { value } = event.target;
    const currentItems = dwelling.rating_information.interior_wall_height || [];
    const updatedItems = [...currentItems];

    if (!updatedItems[itemIndex]) {
      updatedItems[itemIndex] = {
        height_feet: '',
        height_meters: '',
        height_percent: '',
      };
    }

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      [field]: value,
    };

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_wall_height',
        value: updatedItems,
      }),
    );
  };

  const onInteriorFloorFinishChange = (
    event: any,
    itemIndex: number,
    field: string,
  ) => {
    const { value } = event.target;
    const currentItems =
      dwelling.rating_information.interior_floor_finish || [];
    const updatedItems = [...currentItems];

    if (!updatedItems[itemIndex]) {
      updatedItems[itemIndex] = { type: '', percent: '' };
    }

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      [field]: value,
    };

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_floor_finish',
        value: updatedItems,
      }),
    );
  };

  const onCeilingConstructionChange = (
    event: any,
    itemIndex: number,
    field: string,
  ) => {
    const { value } = event.target;
    const currentItems = dwelling.rating_information.ceiling_construction || [];
    const updatedItems = [...currentItems];

    if (!updatedItems[itemIndex]) {
      updatedItems[itemIndex] = { type: '', percent: '' };
    }

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      [field]: value,
    };

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'ceiling_construction',
        value: updatedItems,
      }),
    );
  };

  const addInteriorWallConstructionItem = () => {
    const currentItems =
      dwelling.rating_information.interior_wall_construction || [];
    if (currentItems.length >= 3) return;
    const updatedItems = [...currentItems, { type: '', percent: '' }];

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_wall_construction',
        value: updatedItems,
      }),
    );
  };

  const removeInteriorWallConstructionItem = (itemIndex: number) => {
    const currentItems =
      dwelling.rating_information.interior_wall_construction || [];
    const updatedItems = currentItems.filter(
      (_: any, idx: number) => idx !== itemIndex,
    );

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_wall_construction',
        value: updatedItems,
      }),
    );
  };

  const addInteriorWallHeightItem = () => {
    const currentItems = dwelling.rating_information.interior_wall_height || [];
    if (currentItems.length >= 3) return;
    const updatedItems = [
      ...currentItems,
      { height_feet: '', height_meters: '', height_percent: '' },
    ];

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_wall_height',
        value: updatedItems,
      }),
    );
  };

  const removeInteriorWallHeightItem = (itemIndex: number) => {
    const currentItems = dwelling.rating_information.interior_wall_height || [];
    const updatedItems = currentItems.filter(
      (_: any, idx: number) => idx !== itemIndex,
    );

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_wall_height',
        value: updatedItems,
      }),
    );
  };

  const addInteriorFloorFinishItem = () => {
    const currentItems =
      dwelling.rating_information.interior_floor_finish || [];
    if (currentItems.length >= 3) return;
    const updatedItems = [...currentItems, { type: '', percent: '' }];

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_floor_finish',
        value: updatedItems,
      }),
    );
  };

  const removeInteriorFloorFinishItem = (itemIndex: number) => {
    const currentItems =
      dwelling.rating_information.interior_floor_finish || [];
    const updatedItems = currentItems.filter(
      (_: any, idx: number) => idx !== itemIndex,
    );

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'interior_floor_finish',
        value: updatedItems,
      }),
    );
  };

  const addCeilingConstructionItem = () => {
    const currentItems = dwelling.rating_information.ceiling_construction || [];
    if (currentItems.length >= 3) return;
    const updatedItems = [...currentItems, { type: '', percent: '' }];

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'ceiling_construction',
        value: updatedItems,
      }),
    );
  };

  const removeCeilingConstructionItem = (itemIndex: number) => {
    const currentItems = dwelling.rating_information.ceiling_construction || [];
    const updatedItems = currentItems.filter(
      (_: any, idx: number) => idx !== itemIndex,
    );

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'ceiling_construction',
        value: updatedItems,
      }),
    );
  };

  const ratingInfo = dwelling.rating_information;

  if (!ratingInfo) return null;

  return (
    <Box mb="20px">
      {/* Interior Wall Construction */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Interior Wall Construction
      </Text>
      {(ratingInfo.interior_wall_construction || []).map(
        (item: any, itemIndex: number) => (
          <Box
            key={itemIndex}
            mb="10px"
            p="10px"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="10px"
            >
              <Text fontSize="sm" color="gray.600">
                Item {itemIndex + 1}
              </Text>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                onClick={() => removeInteriorWallConstructionItem(itemIndex)}
              >
                Remove
              </Button>
            </Box>
            <SimpleGrid columns={2} spacing="15px">
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Type"
                name={`interior_wall_construction_type_${itemIndex}`}
                value={item.type || ''}
                onChange={(e: any) =>
                  onInteriorWallConstructionChange(e, itemIndex, 'type')
                }
              />
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Percent"
                name={`interior_wall_construction_percent_${itemIndex}`}
                value={item.percent || ''}
                onChange={(e: any) =>
                  onInteriorWallConstructionChange(e, itemIndex, 'percent')
                }
              />
            </SimpleGrid>
          </Box>
        ),
      )}
      {(ratingInfo.interior_wall_construction || []).length < 3 && (
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          onClick={addInteriorWallConstructionItem}
          mb="15px"
          disabled={isDisabled}
        >
          Add Interior Wall Construction (
          {(ratingInfo.interior_wall_construction || []).length}/3)
        </Button>
      )}

      {/* Interior Wall Height */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Interior Wall Height
      </Text>
      {(ratingInfo.interior_wall_height || []).map(
        (item: any, itemIndex: number) => (
          <Box
            key={itemIndex}
            mb="10px"
            p="10px"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="10px"
            >
              <Text fontSize="sm" color="gray.600">
                Item {itemIndex + 1}
              </Text>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                onClick={() => removeInteriorWallHeightItem(itemIndex)}
              >
                Remove
              </Button>
            </Box>
            <SimpleGrid columns={3} spacing="15px">
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Height Feet"
                name={`interior_wall_height_feet_${itemIndex}`}
                value={item.height_feet || ''}
                onChange={(e: any) =>
                  onInteriorWallHeightChange(e, itemIndex, 'height_feet')
                }
              />
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Height Meters"
                name={`interior_wall_height_meters_${itemIndex}`}
                value={item.height_meters || ''}
                onChange={(e: any) =>
                  onInteriorWallHeightChange(e, itemIndex, 'height_meters')
                }
              />
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Height Percent"
                name={`interior_wall_height_percent_${itemIndex}`}
                value={item.height_percent || ''}
                onChange={(e: any) =>
                  onInteriorWallHeightChange(e, itemIndex, 'height_percent')
                }
              />
            </SimpleGrid>
          </Box>
        ),
      )}
      {(ratingInfo.interior_wall_height || []).length < 3 && (
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          onClick={addInteriorWallHeightItem}
          mb="15px"
          disabled={isDisabled}
        >
          Add Interior Wall Height (
          {(ratingInfo.interior_wall_height || []).length}/3)
        </Button>
      )}

      {/* Interior Floor Finish */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Interior Floor Finish
      </Text>
      {(ratingInfo.interior_floor_finish || []).map(
        (item: any, itemIndex: number) => (
          <Box
            key={itemIndex}
            mb="10px"
            p="10px"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="10px"
            >
              <Text fontSize="sm" color="gray.600">
                Item {itemIndex + 1}
              </Text>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                onClick={() => removeInteriorFloorFinishItem(itemIndex)}
              >
                Remove
              </Button>
            </Box>
            <SimpleGrid columns={2} spacing="15px">
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Type"
                name={`interior_floor_finish_type_${itemIndex}`}
                value={item.type || ''}
                onChange={(e: any) =>
                  onInteriorFloorFinishChange(e, itemIndex, 'type')
                }
              />
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Percent"
                name={`interior_floor_finish_percent_${itemIndex}`}
                value={item.percent || ''}
                onChange={(e: any) =>
                  onInteriorFloorFinishChange(e, itemIndex, 'percent')
                }
              />
            </SimpleGrid>
          </Box>
        ),
      )}
      {(ratingInfo.interior_floor_finish || []).length < 3 && (
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          onClick={addInteriorFloorFinishItem}
          mb="15px"
          disabled={isDisabled}
        >
          Add Interior Floor Finish (
          {(ratingInfo.interior_floor_finish || []).length}/3)
        </Button>
      )}

      {/* Ceiling Construction */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Ceiling Construction
      </Text>
      {(ratingInfo.ceiling_construction || []).map(
        (item: any, itemIndex: number) => (
          <Box
            key={itemIndex}
            mb="10px"
            p="10px"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="10px"
            >
              <Text fontSize="sm" color="gray.600">
                Item {itemIndex + 1}
              </Text>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                onClick={() => removeCeilingConstructionItem(itemIndex)}
              >
                Remove
              </Button>
            </Box>
            <SimpleGrid columns={2} spacing="15px">
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Type"
                name={`ceiling_construction_type_${itemIndex}`}
                value={item.type || ''}
                onChange={(e: any) =>
                  onCeilingConstructionChange(e, itemIndex, 'type')
                }
              />
              <FormInput
                section={SECTIONS.DWELLINGS}
                label="Percent"
                name={`ceiling_construction_percent_${itemIndex}`}
                value={item.percent || ''}
                onChange={(e: any) =>
                  onCeilingConstructionChange(e, itemIndex, 'percent')
                }
              />
            </SimpleGrid>
          </Box>
        ),
      )}
      {(ratingInfo.ceiling_construction || []).length < 3 && (
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          onClick={addCeilingConstructionItem}
          mb="15px"
          disabled={isDisabled}
        >
          Add Ceiling Construction (
          {(ratingInfo.ceiling_construction || []).length}/3)
        </Button>
      )}
    </Box>
  );
};

export default InteriorConstruction;
